import React, { useState, useEffect } from 'react'
import { useLocation, useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useProfile } from '../../contexts/ProfileContext'
import { useTenant } from '../../contexts/TenantContext'
import { usePortalPermissions } from '../../features/auth/usePortalPermissions'
import {
  Search, HelpCircle, LogOut, Building2, ChevronDown,
  User, Sun, Moon, ChevronRight, Settings, CreditCard,
  Terminal, Copy, MonitorPlay, CheckCheck,
} from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './dropdown-menu'
import NotificationCenter from '../NotificationCenter'
import { useTheme } from 'next-themes'
import CommandMenu from '../admin/CommandMenu'
import { BUILD_INFO } from '../../lib/build-info'
import { getBuildInfo, getEnvironmentDisplay, getEnvironmentColor } from '../../lib/buildInfo'

const isDev = import.meta.env.DEV || import.meta.env.VITE_SHOW_DEBUG_MENU === 'true'

// ─── Route title resolver ──────────────────────────────────────────────────────

const ROUTE_LABELS: Record<string, string> = {
  '/dashboard': 'Overview',
  '/people': 'People',
  '/organisation': 'Organisation',
  '/blueprints': 'Blueprints',
  '/my-work': 'My Work',
  '/compliance': 'Compliance',
  '/reports': 'Reports',
  '/settings': 'Settings',
  '/operations': 'AI Operations',
  '/admin/documents': 'Documents',
  '/admin/setup': 'Setup',
}

function resolvePageTitle(pathname: string): string {
  if (ROUTE_LABELS[pathname]) return ROUTE_LABELS[pathname]
  const match = Object.keys(ROUTE_LABELS).find((k) => pathname.startsWith(k) && k !== '/')
  if (match) return ROUTE_LABELS[match]
  const segment = pathname.split('/').filter(Boolean).pop() ?? ''
  return segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ') || 'ODOnboard'
}

function getBreadcrumbs(pathname: string): { label: string; path: string }[] {
  const crumbs: { label: string; path: string }[] = []
  if (pathname === '/dashboard') return crumbs
  if (pathname.startsWith('/people/')) {
    crumbs.push({ label: 'People', path: '/people' })
    return crumbs
  }
  if (pathname.startsWith('/admin/journey-templates/')) {
    crumbs.push({ label: 'Journeys', path: '/admin/journey-templates' })
    return crumbs
  }
  if (pathname.startsWith('/settings')) {
    crumbs.push({ label: 'Settings', path: '/settings' })
    return crumbs
  }
  return crumbs
}

// ─── Theme toggle ─────────────────────────────────────────────────────────────

function ThemeButton() {
  const { resolvedTheme, setTheme } = useTheme()
  const [mounted, setMounted] = useState(false)
  useEffect(() => setMounted(true), [])
  if (!mounted) return <div className="w-7 h-7" />
  return (
    <button
      type="button"
      aria-label="Toggle theme"
      onClick={() => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')}
      style={{ color: 'var(--color-text-secondary)' }}
      className="flex h-7 w-7 items-center justify-center rounded-md transition-colors hover:bg-panel dark:hover:bg-white/8 dark:text-slate-400 dark:hover:text-white"
    >
      {resolvedTheme === 'dark' ? <Sun className="w-[15px] h-[15px]" /> : <Moon className="w-[15px] h-[15px]" />}
    </button>
  )
}

// ─── Search trigger ────────────────────────────────────────────────────────────

function SearchTrigger({ onClick }: { onClick: () => void }) {
  const isMac = typeof navigator !== 'undefined' && /Mac/.test(navigator.platform)
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Open command palette"
      className="hidden md:flex items-center gap-2 h-8 w-full max-w-[256px] rounded-lg px-3 transition-colors duration-150"
      style={{
        background: '#F1F5F9',
        border: '1px solid #E5E7EB',
        color: 'var(--color-text-tertiary)',
      }}
      onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = '#E9EDF2' }}
      onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = '#F1F5F9' }}
    >
      <Search className="w-3.5 h-3.5 flex-shrink-0"
              style={{ color: 'var(--color-text-tertiary)' }} aria-hidden />
      <span className="flex-1 text-left text-[12.5px]"
            style={{ color: 'var(--color-text-tertiary)' }}>
        Search or jump to…
      </span>
      <kbd
        className="flex items-center justify-center h-4 px-1 rounded text-[10px] font-medium"
        style={{
          color: 'var(--color-text-tertiary)',
          background: 'white',
          border: '1px solid #E5E7EB',
          lineHeight: 1,
        }}
      >
        {isMac ? '⌘K' : 'Ctrl K'}
      </kbd>
    </button>
  )
}

// ─── Help dropdown ─────────────────────────────────────────────────────────────

function HelpDropdown() {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Help"
          style={{ color: 'var(--color-text-secondary)' }}
          className="flex h-7 w-7 items-center justify-center rounded-md transition-colors hover:bg-panel dark:text-slate-400 dark:hover:text-white dark:hover:bg-white/8"
        >
          <HelpCircle className="w-[15px] h-[15px]" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        <DropdownMenuLabel className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: 'var(--color-text-tertiary)' }}>
          Help & Support
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => window.open('https://docs.odonboard.com', '_blank')}>
          Documentation
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => window.open('https://docs.odonboard.com/keyboard-shortcuts', '_blank')}>
          Keyboard shortcuts
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => window.open('mailto:support@odonboard.com', '_blank')}>
          Report an issue
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

// ─── Profile dropdown ─────────────────────────────────────────────────────────

function ProfileDropdown({ compact }: { compact?: boolean }) {
  const navigate = useNavigate()
  const { user, signOut } = useAuth()
  const { profile } = useProfile()
  const { tenant } = useTenant()
  const { isEmployee, isSuperadmin } = usePortalPermissions()

  const [devOpen, setDevOpen] = useState(false)
  const [copied,  setCopied]  = useState(false)

  const displayName =
    (profile as any)?.full_name ||
    profile?.name ||
    user?.user_metadata?.full_name ||
    user?.email?.split('@')[0] ||
    'User'
  const displayEmail   = profile?.email || user?.email || ''
  const profileRole    = (profile as any)?.role as string | undefined
  const isOwner        = profileRole === 'owner'
  const isAdminOrOwner = profileRole === 'owner' || profileRole === 'admin'
  const initials       = displayName.charAt(0).toUpperCase()
  const workspaceName  = (tenant as any)?.name ?? null
  const tenantId       = (tenant as any)?.id   ?? null

  // Dev tools visible in dev/preview OR for workspace owners
  const showDevTools = isDev || isOwner

  const buildInfo  = getBuildInfo()
  const envLabel   = getEnvironmentDisplay(buildInfo.env)
  const envColour  = getEnvironmentColor(buildInfo.env)

  const builtAtLabel = (() => {
    try {
      const d = new Date(BUILD_INFO.builtAt)
      return (
        d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) +
        ' · ' +
        d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false })
      )
    } catch { return 'development' }
  })()

  const handleCopy = async () => {
    const lines = [
      `ODOnboard ${BUILD_INFO.label}`,
      `Env: ${envLabel}`,
      `Built: ${builtAtLabel}`,
      tenantId ? `Tenant: ${tenantId}` : null,
    ].filter(Boolean).join('\n')
    try {
      await navigator.clipboard.writeText(lines)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch { /* clipboard unavailable — non-critical */ }
  }

  const handleConsole = () => {
     
    console.log('%c[ODOnboard]', 'color:#4C6FFF;font-weight:bold',
      { version: BUILD_INFO.label, env: envLabel, builtAt: BUILD_INFO.builtAt, tenantId })
  }

  if (!profile && !user) return null

  return (
    <DropdownMenu>
      {/* ── Trigger ── */}
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          // Below `md` the name is hidden and only the initials remain, which
          // are decorative — leaving the control with no accessible name at
          // all on every phone and small tablet. The label is unconditional so
          // it cannot be lost to a breakpoint again.
          aria-label={`Account menu for ${displayName}`}
          className="flex items-center gap-1.5 rounded-lg px-1.5 py-1 transition-colors hover:bg-panel dark:hover:bg-white/8 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4C6FFF]/40"
        >
          {/* Avatar */}
          <div
            aria-hidden
            className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-semibold flex-shrink-0 select-none"
            style={{ background: 'linear-gradient(135deg,#1F2A44,#4866F2)', color: '#fff' }}
          >
            {initials}
          </div>
          {!compact && (
            <>
              <div className="hidden md:block min-w-0">
                <div className="text-[12.5px] font-medium leading-tight truncate max-w-[100px]" style={{ color: '#0F172A' }}>
                  {displayName}
                </div>
              </div>
              <ChevronDown className="w-3 h-3 hidden md:block flex-shrink-0" style={{ color: 'var(--color-text-tertiary)' }} />
            </>
          )}
        </button>
      </DropdownMenuTrigger>

      {/* ── Content ── */}
      <DropdownMenuContent
        align="end"
        sideOffset={8}
        className="p-0 rounded-xl border border-border shadow-[0_20px_50px_rgba(15,23,42,0.16)] overflow-hidden bg-white/[0.98] backdrop-blur-xl data-[state=open]:duration-150 data-[state=open]:zoom-in-[99%]"
        style={{ width: 340 }}
      >
        {/* Identity */}
        <div className="px-4 py-4">
          <div className="flex items-center gap-3">
            {/* 40px avatar */}
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center text-[15px] font-semibold flex-shrink-0 select-none ring-2 ring-white"
              style={{ background: 'linear-gradient(135deg,#1F2A44,#4C6FFF)', color: '#fff', boxShadow: '0 0 0 2px rgba(76,111,255,0.15)' }}
            >
              {initials}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[13.5px] font-semibold truncate leading-snug" style={{ color: '#0F172A' }}>
                {displayName}
              </p>
              <p className="text-xs truncate mt-0.5 leading-snug" style={{ color: '#64748B' }}>{displayEmail}</p>
              {workspaceName && (
                <p className="text-meta truncate mt-1 flex items-center gap-1 leading-snug" style={{ color: 'var(--color-text-tertiary)' }}>
                  <Building2 className="w-2.5 h-2.5 flex-shrink-0" />
                  {workspaceName}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="h-px" style={{ background: '#F1F5F9' }} />

        {/* Primary actions */}
        <div className="py-2">
          <MenuItem icon={<User className="w-3.5 h-3.5" style={{ color: '#64748B' }} />} label="Profile" sub="Account details & preferences" onClick={() => navigate('/settings')} />
          {isAdminOrOwner && <MenuItem icon={<Settings className="w-3.5 h-3.5" style={{ color: '#64748B' }} />} label="Workspace Settings" sub="Team, branding & configuration" onClick={() => navigate('/settings?tab=company')} />}
          {isOwner && <MenuItem icon={<CreditCard className="w-3.5 h-3.5" style={{ color: '#64748B' }} />} label="Billing" sub="Plan, usage & invoices" onClick={() => navigate('/settings?tab=billing')} />}
          <MenuItem icon={<HelpCircle className="w-3.5 h-3.5" style={{ color: '#64748B' }} />} label="Help & Support" sub="Documentation & contact" onClick={() => window.open('https://docs.odonboard.com', '_blank')} external />
        </div>

        {/* Developer Tools — collapsed accordion */}
        {showDevTools && (
          <>
            <div className="h-px" style={{ background: '#F1F5F9' }} />

            {/* Toggle — plain <button>, never closes the dropdown */}
            <button
              type="button"
              onClick={() => setDevOpen(v => !v)}
              className="w-full flex items-center justify-between px-4 py-2 transition-colors hover:bg-background"
            >
              <div className="flex items-center gap-2">
                <Terminal className="w-3 h-3 flex-shrink-0" style={{ color: '#CBD5E1' }} />
                <span className="text-[11.5px] font-medium" style={{ color: 'var(--color-text-tertiary)' }}>Developer Tools</span>
                {/* Monospace micro-label instead of coloured badge */}
                <span className="font-mono text-[9.5px] leading-none" style={{ color: '#CBD5E1' }}>
                  {BUILD_INFO.commit}
                </span>
              </div>
              <ChevronDown
                className="w-3 h-3 transition-transform duration-[150ms]"
                style={{ color: '#CBD5E1', transform: devOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
              />
            </button>

            <AnimatePresence initial={false}>
              {devOpen && (
                <motion.div
                  key="dev"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.15, ease: 'easeInOut' }}
                  style={{ overflow: 'hidden' }}
                >
                  <div className="px-4 pb-4 space-y-3">

                    {/* Build */}
                    <div className="space-y-1.5">
                      <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: 'var(--color-text-tertiary)' }}>Build</p>
                      <div
                        className="rounded-lg p-3 space-y-1.5"
                        style={{ background: '#F8FAFC', border: '1px solid #F1F5F9' }}
                      >
                        <DevRow label="Version"     value={BUILD_INFO.label} mono />
                        <DevRow label="Environment" value={
                          <span className={`text-meta font-medium px-2 py-0.5 rounded-full ${envColour}`}>{envLabel}</span>
                        } />
                        <DevRow label="Commit"      value={BUILD_INFO.commit} mono />
                        <DevRow label="Built"       value={builtAtLabel} />
                      </div>
                    </div>

                    {/* Workspace */}
                    {tenantId && (
                      <div className="space-y-1.5">
                        <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: 'var(--color-text-tertiary)' }}>Workspace</p>
                        <div
                          className="rounded-lg p-3 space-y-1.5"
                          style={{ background: '#F8FAFC', border: '1px solid #F1F5F9' }}
                        >
                          {workspaceName && <DevRow label="Name" value={workspaceName} />}
                          <DevRow label="Tenant ID" value={tenantId} mono truncate />
                        </div>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={handleCopy}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
                        style={{
                          border: '1px solid #E5E7EB',
                          background: 'white',
                          color: '#64748B',
                        }}
                        onMouseEnter={e => (e.currentTarget.style.background = '#F8FAFC')}
                        onMouseLeave={e => (e.currentTarget.style.background = 'white')}
                      >
                        {copied
                          ? <><CheckCheck className="w-3 h-3" style={{ color: '#10B981' }} /> Copied</>
                          : <><Copy className="w-3 h-3" /> Copy</>
                        }
                      </button>
                      <button
                        type="button"
                        onClick={handleConsole}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
                        style={{
                          border: '1px solid #E5E7EB',
                          background: 'white',
                          color: '#64748B',
                        }}
                        onMouseEnter={e => (e.currentTarget.style.background = '#F8FAFC')}
                        onMouseLeave={e => (e.currentTarget.style.background = 'white')}
                      >
                        <MonitorPlay className="w-3 h-3" />
                        Console
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </>
        )}

        {/* Sign out — sits on a faint red-tinted surface */}
        <div className="h-px" style={{ background: '#F1F5F9' }} />
        <div className="py-2" style={{ background: 'rgba(254,242,242,0.5)' }}>
          <DropdownMenuItem
            onClick={signOut}
            className="mx-1.5 rounded-lg px-3 py-2 gap-2.5 cursor-pointer text-danger hover:bg-red-100/60 hover:text-danger focus:bg-red-100/60 focus:text-danger"
          >
            <LogOut className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="text-sm font-medium">Sign out</span>
          </DropdownMenuItem>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

// ─── Menu item helper ─────────────────────────────────────────────────────────
// Consistent two-line item with icon tile + subtle hover lift.

function MenuItem({
  icon,
  label,
  sub,
  onClick,
  external = false,
}: {
  icon: React.ReactNode
  label: string
  sub: string
  onClick: () => void
  external?: boolean
}) {
  return (
    <DropdownMenuItem
      onSelect={external ? (e) => e.preventDefault() : undefined}
      onClick={onClick}
      className="mx-1.5 rounded-lg px-3 py-2.5 gap-3 cursor-pointer transition-all duration-[150ms] hover:bg-background hover:shadow-[0_1px_3px_rgba(15,23,42,0.06)] focus:bg-background"
      style={{ color: '#374151' }}
    >
      <div
        className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors duration-[150ms]"
        style={{ background: '#F1F5F9' }}
      >
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium leading-snug">{label}</p>
        <p className="text-meta leading-snug mt-0.5" style={{ color: 'var(--color-text-tertiary)' }}>{sub}</p>
      </div>
    </DropdownMenuItem>
  )
}

// ─── Dev panel row helper ──────────────────────────────────────────────────────

function DevRow({
  label,
  value,
  mono = false,
  truncate = false,
}: {
  label: string
  value: React.ReactNode
  mono?: boolean
  truncate?: boolean
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-meta flex-shrink-0" style={{ color: 'var(--color-text-tertiary)' }}>{label}</span>
      {typeof value === 'string' ? (
        <span
          className={['text-meta text-right', mono ? 'font-mono' : '', truncate ? 'truncate max-w-[160px]' : ''].join(' ')}
          style={{ color: 'var(--color-text-secondary)' }}
        >
          {value}
        </span>
      ) : value}
    </div>
  )
}

// ─── CommandRow ───────────────────────────────────────────────────────────────

interface CommandRowProps {
  mobile?: boolean
}

export default function CommandRow({ mobile }: CommandRowProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const pageTitle = resolvePageTitle(location.pathname)
  const [commandOpen, setCommandOpen] = useState(false)

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement
      if ((e.metaKey || e.ctrlKey) && e.key === 'k' && !['INPUT', 'TEXTAREA'].includes(target.tagName)) {
        e.preventDefault()
        setCommandOpen(true)
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [])

  if (mobile) {
    return (
      <>
        <div className="flex items-center justify-between gap-2 w-full">
          <span className="text-sm font-semibold truncate min-w-0" style={{ color: '#0F172A' }}>
            {pageTitle}
          </span>
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              type="button"
              onClick={() => setCommandOpen(true)}
              className="flex h-7 w-7 items-center justify-center rounded-md transition-colors hover:bg-panel"
              aria-label="Open command palette"
              style={{ color: 'var(--color-text-secondary)' }}
            >
              <Search className="w-[15px] h-[15px]" />
            </button>
            <NotificationCenter />
            <ProfileDropdown compact />
          </div>
        </div>
        <CommandMenu
          isOpen={commandOpen}
          onClose={() => setCommandOpen(false)}
          onEmployeeClick={(id) => { navigate(`/people/${id}`); setCommandOpen(false) }}
        />
      </>
    )
  }

  return (
    <>
      {/*
        Command strip — tight, engineered, intentional.
        Height driven by content (≈ 44–48px total with padding).
        No heavy card. No floating effect.
        Subtle blur + thin border + hairline shadow.
      */}
      <div
        className="flex items-center gap-3 rounded-lg px-3"
        style={{
          height: 42,
          background: 'rgba(255,255,255,0.92)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          border: '1px solid rgba(229,231,235,0.9)',
          boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
        }}
      >
        {/* Left: breadcrumbs + page title */}
        <div className="flex items-center gap-3 flex-shrink-0 min-w-0">
          <div className="flex items-center gap-1.5 min-w-0">
            {getBreadcrumbs(location.pathname).length > 0 ? (
              <>
                {getBreadcrumbs(location.pathname).map((crumb) => (
                  <span key={crumb.path} className="flex items-center gap-1.5 flex-shrink-0">
                    <Link
                      to={crumb.path}
                      className="text-xs hover:underline truncate max-w-[120px]"
                      style={{ color: 'var(--color-text-secondary)' }}
                    >
                      {crumb.label}
                    </Link>
                    <ChevronRight className="w-3 h-3 flex-shrink-0" style={{ color: 'var(--color-text-tertiary)' }} />
                  </span>
                ))}
                <span className="text-sm font-semibold truncate" style={{ color: '#0F172A' }}>
                  {pageTitle}
                </span>
              </>
            ) : (
              <span
                className="text-sm font-semibold leading-none truncate"
                style={{ color: '#0F172A' }}
              >
                {pageTitle}
              </span>
            )}
          </div>
        </div>

        {/* Divider */}
        <span className="w-px h-4 bg-border flex-shrink-0" />

        {/* Center: search trigger — takes available space */}
        <div className="flex-1 flex items-center min-w-0">
          <SearchTrigger onClick={() => setCommandOpen(true)} />
        </div>

        {/* Right: icon cluster */}
        <div className="flex items-center gap-px flex-shrink-0">
          <HelpDropdown />
          <NotificationCenter />
          <span className="w-px h-3.5 bg-border mx-1" />
          <ProfileDropdown />
        </div>
      </div>

      <CommandMenu
        isOpen={commandOpen}
        onClose={() => setCommandOpen(false)}
        onEmployeeClick={(id) => { navigate(`/people/${id}`); setCommandOpen(false) }}
      />
    </>
  )
}
